import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
} from 'react';
import { Upload } from './universe';
import { Input, MessagePlugin, Button } from 'tdesign-react';
import { useMenuStore, useImageStore } from '../../store';
import { isLogin, splitByCommas } from '../../utils/index.js';
import {
  DEFAULT_ACCEPT_LIST,
  DEFAULT_IMAGE_PREFIX,
  DEFAULT_MAX_FILE_SIZE,
  MESSAGE_DURATION,
  DEFAULT_MESSAGES,
  REGEX,
} from '../../const/constants.js';

const URL_EXP = REGEX.URL;

/**
 * 将 File 转为 base64
 * @param {File} file
 * @returns {Promise<string>}
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function showErrorMsg(msg) {
  MessagePlugin.error({
    content: msg,
    duration: MESSAGE_DURATION,
    className: 'ocrDemo-messgae-error',
    icon: <span className="ocrDemo-icon-errorMsg" />,
  });
}

function getFileExt(name = '') {
  return name.split('.').pop().toLowerCase();
}

function stripBase64Prefix(base64, ext) {
  if (!base64) return base64;
  // bmp 需剥离 data url 前缀，不同系统给出 MIME 可能是
  // image/bmp 或 image/x-ms-bmp，统一按 image/* 前缀处理
  if (ext === 'bmp') return base64.replace(/^data:image\/[^;]+;base64,/, '');
  if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt', 'md', 'txt', 'pdf'].includes(ext)) {
    return base64.replace(/data.+?;base64,/, '');
  }
  return base64;
}

/**
 * 由一批页构造「批次图片对象」：缩略图区的照片组卡片、以及后续识别都用它
 * @param {Array} pages - 该批次的页
 * @param {Object} [curImage] - 当前 currentImage（可复用其已渲染的页图）
 * @returns {Object|null}
 */
export const buildBatchImage = (pages, curImage) => {
  if (!pages?.length) return null;
  const first = pages[0];
  return {
    ...(curImage?.isMultiUpload ? curImage : {}),
    isMultiUpload: true,
    isExample: false,
    // 批次唯一标识：上传列表按它去重。每张上传页都有唯一 uid，
    // 同一张图片分多次上传会生成不同 uid，避免按 url 去重时不同批次互相替换
    uid: `batch-${pages.map((p) => p.uid || p.url).join('_')}`,
    multiPages: pages,
    pdfImgList:
      curImage?.pdfImgList?.length > 0
        ? curImage.pdfImgList
        : pages
            .filter((p) => (p.fileType || '').toLowerCase() !== 'pdf')
            .map((p, idx) => ({
              ImageUrl: p.previewUrl || p.url,
              pageNum: idx + 1,
            })),
    previewUrl: first.previewUrl,
    url: first.url,
    OriginUrl: first.OriginUrl,
    fileType: first.fileType,
    prefix: first.prefix,
    cantPreview: false,
    cantPreviewMsg: '',
    pageNum: 1,
  };
};

/**
 * 多图上传入口：上传本地文件按钮 + URL 输入
 * 多图菜单下替代单图上传组件，两个 tab 都展示
 */
export function MultiUploadDropzone({ cRef, onBatchReady }) {
  const currentMenu = useMenuStore((s) => s.currentMenu);
  const multiUploadPages = useImageStore((s) => s.multiUploadPages);
  const pdfPreviewPages = useImageStore((s) => s.pdfPreviewPages);
  const pdfPreviewState = useImageStore((s) => s.pdfPreviewState);
  const setPdfPreviewPages = useImageStore((s) => s.setPdfPreviewPages);
  const addMultiUploadPages = useImageStore((s) => s.addMultiUploadPages);
  const clearMultiUploadPages = useImageStore((s) => s.clearMultiUploadPages);
  const setCurrentImage = useImageStore((s) => s.setCurrentImage);
  const setImgListType = useImageStore((s) => s.setImgListType);
  const setTotalPage = useImageStore((s) => s.setTotalPage);
  const setIsImageError = useImageStore((s) => s.setIsImageError);

  const acceptList = currentMenu?.acceptList || DEFAULT_ACCEPT_LIST;
  const prefix = currentMenu?.prefix || DEFAULT_IMAGE_PREFIX;
  const maxLength = currentMenu?.maxLength || DEFAULT_MAX_FILE_SIZE;

  const [imgUrl, setImgUrl] = useState('');
  const clickBtn = useRef();

  const acceptStr = acceptList.map((e) => `.${e}`).join(',');

  // 供外部（缩略图区的"+"上传入口）触发文件选择
  useImperativeHandle(cRef, () => ({
    onUpload: () => {
      clickBtn.current?.click();
    },
  }));

  // 页列表变化时，同步一个"合成的 currentImage"，供右侧参数面板和"开始识别"按钮使用
  // 同时把 tab 定位到「上传文件」
  useEffect(() => {
    if (!multiUploadPages || multiUploadPages.length === 0) return;
    const first = multiUploadPages[0];
    const listPrefix =
      first.type === 'url' ? `${prefix}UrlList` : `${prefix}Base64List`;
    // 图片页可直接用 <img> 预览；pdf 用「前端本地逐页渲染」的结果
    const previewablePages = multiUploadPages.filter(
      (p) => (p.fileType || '').toLowerCase() !== 'pdf'
    );
    const hasPdfPage = multiUploadPages.some(
      (p) => (p.fileType || '').toLowerCase() === 'pdf'
    );
    const pagePreviews =
      previewablePages.length > 0
        ? previewablePages.map((p, idx) => ({
            ImageUrl: p.previewUrl,
            pageNum: idx + 1,
          }))
        : (pdfPreviewPages || []).map((p, idx) => ({
            ImageUrl: p.ImageUrl,
            // 先带本地渲染尺寸，后端返回坐标框后会替换为后端坐标系宽高
            Width: p.Width,
            Height: p.Height,
            pageNum: idx + 1,
          }));
    setCurrentImage({
      isMultiUpload: true,
      multiPages: multiUploadPages,
      // 让预览侧的多页 Thumbnail 复用 pdfImgList 结构（每项含 ImageUrl）
      pdfImgList: pagePreviews,
      previewUrl: pagePreviews[0]?.ImageUrl || first.previewUrl,
      url: first.url,
      OriginUrl: first.OriginUrl,
      fileType: first.fileType,
      // prefix 仅用于 Request 展示（实际发送时按 List key 覆盖）
      prefix: first.prefix,
      // 供 GeneralBasicOCR Async 分支识别的 List key
      listPrefix,
      isExample: false,
      // PDF 本地渲染完成前先占位，渲染好后自动切到页图
      cantPreview: pagePreviews.length === 0,
      // 只有真的在渲染时才说「正在解析」；未开始 / 失败都要给出各自准确的文案，
      // 否则 url 方式上传的 PDF 会永远停在「正在解析 PDF 页…」
      cantPreviewMsg:
        pagePreviews.length === 0 && hasPdfPage && previewablePages.length === 0
          ? pdfPreviewState === 'loading'
            ? '正在解析 PDF 页…'
            : pdfPreviewState === 'failed'
              ? '该 PDF 前端无法直接预览（可能受跨域限制），点击「抽取」后查看结果'
              : '已上传 PDF，点击「抽取」后按页展示'
          : '',
      type: first.type,
      pageNum: 1,
    });
    setTotalPage(multiUploadPages.length);
    setImgListType('upload');
    setIsImageError(false);
  }, [
    multiUploadPages,
    pdfPreviewPages,
    pdfPreviewState,
    prefix,
    setCurrentImage,
    setTotalPage,
    setImgListType,
    setIsImageError,
  ]);

  /**
   * 校验是否与已有页类型一致：拒绝跨类型（url/base64）混批
   * @param {Array} newPages 新增页
   * @returns {boolean}
   */
  const guardMixType = (newPages) => {
    // 必须读 store 实时值：startNewBatchIfNeeded 刚清空了批次，闭包里的 multiUploadPages 仍是上一批
    const { multiUploadPages: livePages } = useImageStore.getState();
    if (!livePages || livePages.length === 0) return true;
    const existingType = livePages[0].type;
    const bad = newPages.some((p) => p.type !== existingType);
    if (bad) {
      showErrorMsg('同一次多图上传不支持混合 URL 和本地文件，请分别提交');
      return false;
    }
    return true;
  };

  // ============ 批次边界：一次上传动作 = 一次入参 ============
  // 同一次选择（多个文件共用一个 fileList 引用）内的文件属于同一批；
  // 换了一次选择、或一次 URL 输入，就是新的一批，先把上一批归档到上传列表
  const batchKeyRef = useRef(null);

  const archiveCurrentBatch = () => {
    const {
      multiUploadPages: pages,
      currentImage: cur,
      addUploadImage: addToUploadList,
    } = useImageStore.getState();
    // 只有 currentImage 确实是「正在归档的这一批」时才复用它已渲染的页图；
    // 若用户在上传下一批前点过其它缩略图，currentImage 已切走，复用会把
    // 别的批次的 pdfImgList/预览图错误地铺到本批，导致归档后缩略图/预览串批。
    const sameBatch =
      cur?.isMultiUpload &&
      cur?.multiPages?.[0]?.uid &&
      cur.multiPages[0].uid === pages?.[0]?.uid;
    const batchImage = buildBatchImage(pages, sameBatch ? cur : null);
    if (!batchImage) return;
    // 归档成上传列表里的一条独立记录，保留自己的页图与识别结果。
    // append=true：按上传先后追加到尾部，避免后归档的批次插到前面导致顺序颠倒
    addToUploadList(batchImage, true);
  };

  /**
   * 新的上传动作到来时：归档上一批并清空当前批次
   * @param {*} batchKey 同一次上传动作的标识（文件选择用 fileList 引用，URL 输入用唯一值）
   */
  const startNewBatchIfNeeded = (batchKey) => {
    if (batchKeyRef.current === batchKey) return;
    batchKeyRef.current = batchKey;
    archiveCurrentBatch();
    clearMultiUploadPages();
  };

  /**
   * 触发 PDF 本地逐页渲染（用于预览底图）
   * - 本地文件：传 base64
   * - 在线 URL：把 URL 交给 pdf.js 拉取（会受对方 COS 跨域配置限制）
   *
   * 不走后端返回的 ImageZipUrl —— 它落在别的业务 COS，浏览器直连会被跨域拦。
   *
   * @param {string} source base64 或 http(s) URL
   */
  const startPdfPreview = (source) => {
    if (!source) return;
    setPdfPreviewPages([], 'loading');
    // 动态引入 pdf.js（独立 chunk，不影响首屏）
    import('../../utils/pdfPreview.js')
      .then(({ renderPdfPagesToImages }) => renderPdfPagesToImages(source))
      .then((pages) => {
        setPdfPreviewPages(pages, pages.length > 0 ? 'done' : 'failed');
      })
      .catch((error) => {
        console.warn('[PDF] 本地预览渲染失败:', error);
        setPdfPreviewPages([], 'failed');
      });
  };

  // 本批次的「期望文件数 / 已处理文件数」：
  // 同一次选择的所有文件会同步进入 beforeUpload，所以期望数在任何异步完成前就已确定，
  // 两者相等即说明这一批已经收齐，可以自动发起识别
  const batchProgressRef = useRef({ key: null, expected: 0, done: 0 });

  const markBatchExpected = (key, isAccepted) => {
    if (batchProgressRef.current.key !== key) {
      batchProgressRef.current = { key, expected: 0, done: 0 };
    }
    if (isAccepted) batchProgressRef.current.expected += 1;
  };

  const markBatchDone = (key) => {
    const progress = batchProgressRef.current;
    if (progress.key !== key) return; // 已经切到新批次，旧回调忽略
    progress.done += 1;
    if (progress.expected > 0 && progress.done >= progress.expected) {
      onBatchReady?.();
    }
  };

  const beforeUpload = (file, fileList, isAccepted) => {
    const batchKey = fileList || file;
    // 先登记期望数量（同步执行，保证收齐判断准确）
    markBatchExpected(batchKey, isAccepted !== false);

    return new Promise(async (_, reject) => {
      try {
        await isLogin();
        if (!file) {
          showErrorMsg(DEFAULT_MESSAGES.SELECT_FILE);
          return reject();
        }
        const ext = getFileExt(file.name);
        if (!acceptList.includes(ext)) {
          showErrorMsg(
            currentMenu?.uploadExtErrorMsg ? (
              <>
                体验页面不支持{ext}格式，请点击
                <a
                  style={{ color: '#4A70FF' }}
                  target="_blank"
                  rel="noreferrer"
                  href={currentMenu.url}
                >
                  {currentMenu.title}
                </a>
                调用API接口
              </>
            ) : (
              DEFAULT_MESSAGES.INVALID_FILE_FORMAT
            )
          );
          return reject();
        }
        const base64 = await fileToBase64(file);
        if (base64?.length > maxLength * 1024 * 1024) {
          showErrorMsg(
            `${DEFAULT_MESSAGES.FILE_TOO_LARGE_BASE641}${maxLength}M${DEFAULT_MESSAGES.FILE_TOO_LARGE_BASE642}`
          );
          return reject();
        }
        const stripped = stripBase64Prefix(base64, ext);
        const newPage = {
          uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          type: 'base64',
          previewUrl: base64,
          OriginUrl: stripped,
          url: stripped,
          base64prefix: '',
          prefix: `${prefix}Base64`,
          fileType: ext,
          isExample: false,
          pageNum: 1,
          cantPreview: false,
          name: file.name,
          size: file.size,
        };
        // 新的一次文件选择 → 新的一批入参；
        // 同一次选择内的多个文件共用一个 fileList 引用，仍累加为同一批
        startNewBatchIfNeeded(batchKey);

        if (!guardMixType([newPage])) {
          return reject();
        }
        addMultiUploadPages([newPage]);

        // PDF：本地逐页渲染作为预览底图
        if (ext === 'pdf') {
          startPdfPreview(base64);
        }
      } catch (err) {
        // ignore
      } finally {
        // 无论成功失败都记一次「已处理」，用于判断本批是否收齐
        markBatchDone(batchKey);
      }
      // 始终 reject 阻断 Upload 组件的默认 request
      return reject();
    });
  };

  const handleCheckUrl = () => {
    const raw = imgUrl.trim();
    if (!raw) return;

    // 支持一次输入多个链接：中英文逗号 / 换行 / 空格分隔。
    // 注意 URL_EXP 是非锚定正则且字符类含逗号，不拆分会把「a.jpg,b.jpg」当成一条 URL
    const urlList = splitByCommas(raw)
      .flatMap((item) => item.split(/\s+/))
      .filter(Boolean);

    const uploadExtList = currentMenu?.uploadExtList || [];
    const allExts = uploadExtList.concat(acceptList);
    const isSingle = urlList.length === 1;

    const newPages = [];
    let invalidCount = 0;

    urlList.forEach((url) => {
      if (!URL_EXP.test(url)) {
        invalidCount += 1;
        return;
      }
      const matched = allExts.filter((e) => url.toLowerCase().indexOf(e) > -1);
      const ext = matched.length > 0 ? matched[matched.length - 1] : '';
      if (!acceptList.includes(ext)) {
        invalidCount += 1;
        return;
      }
      newPages.push({
        uid: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${newPages.length}`,
        type: 'url',
        url,
        OriginUrl: url,
        previewUrl: url,
        prefix: `${prefix}Url`,
        fileType: ext,
        isExample: false,
        pageNum: 1,
        cantPreview: false,
      });
    });

    // 单条输入沿用原来的精确提示；多条输入给汇总提示
    if (newPages.length === 0) {
      if (isSingle) {
        showErrorMsg(
          URL_EXP.test(urlList[0])
            ? DEFAULT_MESSAGES.INVALID_FILE_FORMAT
            : DEFAULT_MESSAGES.INVALID_URL
        );
      } else {
        showErrorMsg(
          `未识别到可用链接，请检查格式（支持${acceptList.join('、')}）`
        );
      }
      return;
    }

    // 一次 URL 输入 = 新的一批入参
    startNewBatchIfNeeded(`url-${Date.now()}`);

    if (!guardMixType(newPages)) return;
    addMultiUploadPages(newPages);

    // 在线 URL 的 PDF 不做前端本地渲染：外链落在对方 COS、未配 CORS，
    // 浏览器 fetch 必然跨域失败（卡「正在解析」或报错）。改为等识别结果用
    // 后端逐页返回的 ReturnImageUrl 作为底图（见 OcrLayout.handleQuestionOCR）。
    // 这里保持 pdfPreviewState=idle（startNewBatchIfNeeded 已重置），预览区显示
    // 「已上传 PDF，点击抽取后按页展示」的中性提示，不卡不报错。
    // 注意：URL 方式的图片（非 PDF）previewUrl 就是图片地址，可直接 <img> 预览，不受影响。

    setImgUrl('');

    if (invalidCount > 0) {
      showErrorMsg(
        `已添加 ${newPages.length} 个链接，${invalidCount} 个格式不支持已忽略`
      );
    }

    // 一次 URL 输入是同步完成的，直接视为该批就绪
    onBatchReady?.();
  };

  const acceptText = acceptList.join('、');
  // 上传提示直接作为输入框占位文案展示
  const uploadHint = `支持上传1份多页PDF，或多张图片，或输入多个URL发起多页切题处理，支持${acceptText}等格式，大小不超过${maxLength}M`;

  return (
    <div className="ocr_upload__container">
      <div className="ocr_upload__width">
        <Upload multiple beforeUpload={beforeUpload} accept={acceptStr}>
          <Button type="button" variant="base" ref={clickBtn} width="full">
            上传本地文件
          </Button>
        </Upload>
        <div className="unv-upload__btn-extra">
          <span className="unv-upload__btn-extra__tips">或</span>
          <Input
            prefixIcon={<span className="ocrDemo-icon_search" />}
            suffixIcon={
              <span className="ocrDemo-icon_enter" onClick={handleCheckUrl} />
            }
            style={{ width: '100%' }}
            value={imgUrl}
            onEnter={handleCheckUrl}
            onChange={(v) => setImgUrl(v)}
            placeholder={uploadHint}
          />
        </div>
      </div>
    </div>
  );
}

export default MultiUploadDropzone;
